import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useToast } from 'components/toast/ToastProvider';
import { useAuthStatus } from 'hooks/useAuthStatus';
import { useSubscriptionAccess } from 'hooks/useSubscriptionAccess';
import { PATHS } from 'routes/paths';
import { authStorage } from 'utils/storage';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAuthenticated = useAuthStatus();
  const hasSubscriptionAccess = useSubscriptionAccess();
  const navigate = useNavigate();
  const { showSuccess } = useToast();

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: PATHS.home },
    { name: 'Library', path: PATHS.library },
    ...(hasSubscriptionAccess ? [] : [{ name: 'Pricing', path: PATHS.pricing }]),
    { name: 'Unsubscribe', path: PATHS.unsubscribe },
  ];

  const handleLogout = () => {
    authStorage.clearToken();
    setMobileOpen(false);
    showSuccess('Logged out successfully');
    navigate(PATHS.home);
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[40] bg-[#2e3543]/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
      ></div>

      <div
        className={`z-50 w-full flex justify-center px-4 sm:px-8 pointer-events-none transition-all duration-500 top-6 ${
          scrolled ? 'fixed left-0 right-0 top-4' : 'sticky'
        }`}
      >
        <header
          className={`w-full max-w-7xl bg-[var(--bg-header)] backdrop-blur-xl rounded-2xl flex lg:grid lg:grid-cols-3 items-center justify-between px-5 py-3.5 pointer-events-auto transition-all relative border border-[var(--border-glass)] ${
            scrolled ? 'py-2 shadow-lg' : 'py-4'
          }`}
        >
          <div className="flex justify-start">
            <Link to={PATHS.home} onClick={() => setMobileOpen(false)}>
              <div className="flex items-center gap-2 pl-2 cursor-pointer group">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-[var(--primary)] group-hover:scale-110 transition-transform duration-500">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" />
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-base font-extrabold text-[#2e3543] tracking-tight">LearnSocialAI</span>
              </div>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-[14px] font-bold text-slate-500 hover:text-[#2e3543] transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-4 sm:gap-5">
            {isAuthenticated ? (
              <>
                <Link to={PATHS.account} className="hidden sm:flex lg:flex items-center justify-center w-10 h-10 rounded-full bg-[#2e3543] hover:bg-slate-800 transition-all shadow-sm">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5">
                    <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                  </svg>
                </Link>
                <button
                  className="hidden lg:block bg-[#2e3543] text-white px-5 py-2.5 rounded-xl text-xs font-black tracking-widest hover:scale-105 transition-all shadow-sm"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to={PATHS.signup} className="hidden lg:block bg-[#2e3543] text-white px-5 py-2.5 rounded-xl text-xs font-black tracking-widest hover:scale-105 transition-all shadow-sm">
                Sign up
              </Link>
            )}

            <button
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 text-[#2e3543] active:scale-95 transition-transform"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
              </svg>
            </button>
          </div>

          <div
            className={`absolute top-full left-0 w-full mt-4 bg-white border border-gray-100 rounded-[2rem] shadow-2xl p-6 sm:p-8 flex flex-col transition-all duration-300 origin-top lg:hidden ${
              mobileOpen ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto' : 'opacity-0 -translate-y-4 scale-95 pointer-events-none'
            }`}
          >
            <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
              <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Menu</span>
              <button
                className="p-2 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#2e3543" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <nav className="flex flex-col gap-6 mb-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-lg font-bold text-[#2e3543] hover:translate-x-2 transition-transform"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="mt-auto space-y-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to={PATHS.account}
                    className="flex items-center gap-4 p-4 mb-2 bg-gray-50 rounded-2xl border border-gray-100"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#2e3543] flex items-center justify-center">
                      <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-7 8-7s8 3 8 7" /></svg>
                    </div>
                    <span className="font-bold text-[#2e3543]">My Account</span>
                  </Link>
                  <button
                    type="button"
                    className="block w-full bg-white border-2 border-gray-100 text-[#2e3543] text-center py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:border-[#2e3543] transition-colors"
                    onClick={handleLogout}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <Link
                  to={PATHS.signup}
                  className="block w-full bg-[#2e3543] text-white text-center py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform"
                  onClick={() => setMobileOpen(false)}
                >
                  Sign up
                </Link>
              )}
            </div>
          </div>
        </header>
      </div>
    </>
  );
};

export default Header;
